import React, { useMemo } from 'react';
import { List, ListItem, ListItemText, Chip, Box, Divider, IconButton, Typography, Dialog, DialogTitle, DialogContent, DialogActions, Button } from '@mui/material';
import { Link } from 'react-router-dom';
import * as Icons from '@mui/icons-material';
import { useTheme } from '@mui/material';
import { useMediaQuery } from '@mui/material';
import { Delete as DeleteIcon } from '@mui/icons-material';

function RecipeList({ filteredRecipes, sortOrder, inventory, favorites, bookmarks, toggleFavorite, toggleBookmark, nameCounts, searchTerms }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Compute groupedRecipes unconditionally
  const groupedRecipes = useMemo(() => {
    return filteredRecipes.reduce((acc, recipe) => {
      const category = recipe.category || 'Uncategorized';
      acc[category] = acc[category] || [];
      acc[category].push(recipe);
      return acc;
    }, {});
  }, [filteredRecipes]);

  // Compute sortedCategories unconditionally
  const sortedCategories = useMemo(() => {
    return Object.keys(groupedRecipes).sort((a, b) => a.localeCompare(b));
  }, [groupedRecipes]);

  // Sort recipes within each category unconditionally
  useMemo(() => {
    sortedCategories.forEach(category => {
      groupedRecipes[category].sort((a, b) => a.name.localeCompare(b.name));
    });
  }, [sortedCategories, groupedRecipes]);

  // Compute sortedRecipes unconditionally
  const sortedRecipes = useMemo(() => {
    if (sortOrder === 'asc') {
      return [...filteredRecipes].sort((a, b) => a.name.localeCompare(b.name));
    } else if (sortOrder === 'desc') {
      return [...filteredRecipes].sort((a, b) => b.name.localeCompare(a.name));
    }
    return filteredRecipes;
  }, [filteredRecipes, sortOrder]);

  // Compute groupedByLetter unconditionally
  const groupedByLetter = useMemo(() => {
    if (sortOrder === 'asc' || sortOrder === 'desc') {
      return sortedRecipes.reduce((acc, recipe) => {
        const firstChar = recipe.name[0];
        const letter = /^\d/.test(firstChar) ? '#' : firstChar.toUpperCase();
        acc[letter] = acc[letter] || [];
        acc[letter].push(recipe);
        return acc;
      }, {});
    }
    return {};
  }, [sortedRecipes, sortOrder]);

  // If there are no filtered recipes, render a friendly message.
  if (filteredRecipes.length === 0) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography variant="body1" color="text.secondary">
          No results found
        </Typography>
      </Box>
    );
  }

  const renderRecipeItem = (recipe) => {
    const displayName = nameCounts[recipe.name] > 1 ? `${recipe.name} (${recipe.origin})` : recipe.name;

    // Get the ingredient list as a string and highlight matching search terms
    const ingredientList = recipe.ingredients
      .map(ing => {
        // Use the name field directly, fallback to ingredientId if name is not available
        return ing.name || (ing.ingredientId && ing.ingredientId.includes('-') ? 
          ing.ingredientId.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ') : 
          ing.ingredientId);
      })
      .join(', ');

    // Function to highlight ingredients matching searchTerms
    const highlightIngredients = (text) => {
      if (!searchTerms || searchTerms.length === 0) return text;
      let result = text;
      searchTerms.forEach(term => {
        const lowerTerm = term.toLowerCase();
        // Check if the term matches an ingredient name in inventory
        if (inventory.some(ing => ing.name.toLowerCase() === lowerTerm)) {
          const regex = new RegExp(`\\b${term}\\b`, 'gi');
          result = result.replace(
            regex,
            match => `<span style="background-color: ${theme.palette.mode === 'dark' ? '#4db6ac' : '#b2dfdb'}; color: ${theme.palette.text.primary}; padding: 0 2px; border-radius: 2px;">${match}</span>`
          );
        }
      });
      return result;
    };

    const formattedIngredients = highlightIngredients(ingredientList);

    return (
      <ListItem
        key={recipe.id}
        component={Link}
        to={`/recipe/${recipe.id}`}
        state={{ from: { type: 'collection', id: 'my-recipes' } }}
        onClick={() => {
          window.scrollTo(0, 0);
          document.body.scrollTop = 0;
          document.documentElement.scrollTop = 0;
          const scrollContainers = document.querySelectorAll('[style*="overflow"]');
          scrollContainers.forEach(container => {
            container.scrollTop = 0;
          });
        }}
        sx={{
          alignItems: 'flex-start',
          py: { xs: 0.75, sm: 0.25 },
          position: 'relative',
          backgroundColor: 'transparent',
          transitionProperty: 'background-color',
          transitionDuration: '150ms',
          transitionTimingFunction: 'ease-out',
          '&:hover': {
            backgroundColor: theme.palette.action.hover,
            borderRadius: { xs: 2, sm: 1 },
            textDecoration: 'none',
          },
          '&:active': {
            backgroundColor: theme.palette.action.selected,
          },
          px: { xs: 1, sm: 2 },
          textDecoration: 'none',
          color: 'inherit',
          minHeight: { xs: '48px', sm: 'auto' },
        }}
      >
        <ListItemText
          primary={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Chip
                icon={recipe.missingCount === 0 ? <Icons.Check fontSize="small" /> : undefined}
                label={recipe.missingCount === 0 ? '' : recipe.missingCount}
                color={recipe.missingCount === 0 ? 'success' : 'error'}
                size="small"
                sx={{
                  mr: 1,
                  width: { xs: '24px', sm: '20px' },
                  height: { xs: '24px', sm: '20px' },
                  borderRadius: '50%',
                  fontSize: { xs: '12px', sm: '10px' },
                  padding: 0,
                  minWidth: 'unset',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  '& .MuiChip-icon': { margin: 0, fontSize: { xs: '16px', sm: '14px' } },
                  '& .MuiChip-label': { display: recipe.missingCount === 0 ? 'none' : 'block', padding: 0 },
                }}
              />
              <Box
                sx={{
                  flex: 1,
                  fontSize: { xs: '1rem', sm: '0.875rem' },
                  fontWeight: isMobile ? 500 : 400,
                  lineHeight: 1.2,
                }}
              >
                {displayName}
              </Box>
              <IconButton
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  toggleFavorite(recipe.id);
                }}
                sx={{
                  color: favorites.has(recipe.id) ? 'error.main' : 'inherit',
                  padding: { xs: '6px', sm: '4px' },
                }}
              >
                {favorites.has(recipe.id) ? (
                  <Icons.Favorite fontSize={isMobile ? 'medium' : 'small'} />
                ) : (
                  <Icons.FavoriteBorder fontSize={isMobile ? 'medium' : 'small'} />
                )}
              </IconButton>
              <IconButton
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  toggleBookmark(recipe.id);
                }}
                sx={{
                  color: bookmarks.has(recipe.id) ? 'primary.main' : 'inherit',
                  padding: { xs: '6px', sm: '4px' },
                }}
              >
                {bookmarks.has(recipe.id) ? (
                  <Icons.Bookmark fontSize={isMobile ? 'medium' : 'small'} />
                ) : (
                  <Icons.BookmarkBorder fontSize={isMobile ? 'medium' : 'small'} />
                )}
              </IconButton>
            </Box>
          }
          secondary={
            <span
              dangerouslySetInnerHTML={{ __html: formattedIngredients }}
            />
          }
          secondaryTypographyProps={{
            sx: {
              fontSize: { xs: '0.7rem', sm: '0.65rem' },
              color: theme.palette.text.secondary,
              mt: { xs: 0.25, sm: 0 },
              display: 'block',
              lineHeight: { xs: 1.1, sm: 1.2 },
            },
          }}
        />
      </ListItem>
    );
  };

  return (
    <>
      <List
        dense={!isMobile}
        sx={{
          backgroundColor: theme.palette.background.paper,
          borderRadius: { xs: 2, sm: 1 },
          padding: 0,
          flex: 1,
          overflow: 'visible',
        }}
      >
        {sortOrder === 'category'
          ? sortedCategories.map(category => {
              const recipesForCategory = groupedRecipes[category].filter(r => r);
              if (recipesForCategory.length === 0) return null;
              return (
                <React.Fragment key={category}>
                  <ListItem
                    sx={{
                      paddingLeft: 2,
                      paddingTop: 0,
                      paddingBottom: 0,
                      backgroundColor: 'transparent',
                      position: 'sticky',
                      top: { xs: '60px', sm: '80px' },
                      zIndex: 5,
                      minHeight: 'auto',
                    }}
                  >
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: 'bold',
                        color: 'text.secondary',
                        fontSize: { xs: '0.9rem', sm: '0.8rem' },
                      }}
                    >
                      {category}
                    </Typography>
                  </ListItem>
                  {recipesForCategory.map(recipe => (
                    <React.Fragment key={recipe.id}>{renderRecipeItem(recipe)}</React.Fragment>
                  ))}
                  <Divider sx={{ my: 0.5 }} />
                </React.Fragment>
              );
            })
          : Object.keys(groupedByLetter)
              .sort((a, b) => {
                // Handle special case for '#' (numeric recipes)
                if (a === '#') return sortOrder === 'asc' ? 1 : -1;  // Move # to end in asc, beginning in desc
                if (b === '#') return sortOrder === 'asc' ? -1 : 1;  // Move # to end in asc, beginning in desc
                // Normal alphabetical sorting based on current sortOrder
                return sortOrder === 'asc' ? a.localeCompare(b) : b.localeCompare(a);
              })
              .map(letter => {
                const recipesForLetter = groupedByLetter[letter].filter(r => r);
                if (recipesForLetter.length === 0) return null;
                return (
                  <React.Fragment key={letter}>
                    <ListItem
                      sx={{
                        paddingLeft: 2,
                        paddingTop: 0,
                        paddingBottom: 0,
                        backgroundColor: 'transparent',
                        position: 'sticky',
                        top: { xs: '60px', sm: '80px' },
                        zIndex: 5,
                        minHeight: 'auto',
                      }}
                    >
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 'bold',
                          color: 'text.secondary',
                          fontSize: { xs: '0.9rem', sm: '0.8rem' },
                        }}
                      >
                        {letter}
                      </Typography>
                    </ListItem>
                    {recipesForLetter.map(recipe => (
                      <React.Fragment key={recipe.id}>{renderRecipeItem(recipe)}</React.Fragment>
                    ))}
                    <Divider sx={{ my: 0.5 }} />
                  </React.Fragment>
                );
              })}
      </List>
    </>
  );
}

export default RecipeList;